import { useEffect, useMemo } from "react";

import { useGetAccountsQuery } from "../../../services";
import {
  cashbackSourceAccountsOf,
  cashbackSourceOptionsOf,
  hasCashback,
} from "../../../utils/cashback";
import { CashbackFunding, UseCashbackFundingParams } from "./types";

export const useCashbackFunding = ({
  isEnabled,
  investmentAccountId,
  cashbackSource,
  recorded,
  setCashbackSource,
}: UseCashbackFundingParams): CashbackFunding => {
  const { data: accounts } = useGetAccountsQuery();
  const isCorrection = recorded !== undefined;

  // An unresolved broker has no currency to match, so nothing is offered rather than every currency.
  const brokerCurrency = accounts?.find(
    (account) => account.id === investmentAccountId,
  )?.currencyCode;
  const sourceAccounts = useMemo(
    () => cashbackSourceAccountsOf(accounts, brokerCurrency),
    [accounts, brokerCurrency],
  );

  const isShown =
    isEnabled &&
    brokerCurrency !== undefined &&
    (Boolean(recorded) ||
      sourceAccounts.some((account) => hasCashback(account.cashbackMode)));

  const sourceOptions = cashbackSourceOptionsOf({
    accounts,
    currencyCode: brokerCurrency,
    selected: cashbackSource,
  });

  // Only one cashback account to pick from on a new buy, so it is picked for the user.
  useEffect(() => {
    if (!isShown || isCorrection || cashbackSource) {
      return;
    }
    const earning = sourceAccounts.filter((account) =>
      hasCashback(account.cashbackMode),
    );
    if (earning.length === 1) {
      setCashbackSource({
        value: String(earning[0].id),
        label: earning[0].name,
      });
    }
  }, [
    isShown,
    isCorrection,
    cashbackSource,
    sourceAccounts,
    setCashbackSource,
  ]);

  return { isShown, sourceOptions };
};
